import React, { useState, useContext, useEffect } from "react";
import { Box, Typography, Stack, Button, Grid, TextField } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { API, graphqlOperation, Storage } from "aws-amplify";
import {
  createInvoice,
  updateUser,
  updateInvoice,
  createPart,
} from "../../graphql/mutations";
import { getInvoice, getPricelist } from "../../graphql/queries";
import { AppContext } from "../../App";
import { calculateInvoicePrice } from "../../utils/invoicePricingUtils";
import { PDFDownloadLink } from "@react-pdf/renderer";
import InvoicePDF from "./InvoicePDF";
import PrintIcon from "@mui/icons-material/Print";
import InnerPage from "../InnerPage";
import {
  fetchUsers,
  fetchCustomersForProvider,
} from "../../services/userManagementService";

// Import refactored components
import InvoiceHeader from "./InvoiceHeader";
import InvoiceStats from "./InvoiceStats";
import PartsList from "./PartsList";
import ActionButtons from "./ActionButtons";
import {
  SearchModal,
  CustomPartModal,
  UnitByWeightModal,
} from "./InvoiceModals";
import { CustomerSelector, CustomerCard } from "../shared";
import UserDialog from "../shared/UserDialog";

// Custom query to get invoice with parts
const getInvoiceWithParts = /* GraphQL */ `
  query GetInvoice($id: ID!) {
    getInvoice(id: $id) {
      id
      name
      invoiceNumber
      status
      ownerID
      recipientID
      partCount
      createdAt
      updatedAt
      parts {
        items {
          id
          partNumber
          price
          grade
          status
          createdAt
          updatedAt
        }
      }
    }
  }
`;

export default function CreateInvoice() {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { userRecord } = useContext(AppContext);
  const { invoiceId } = useParams();

  // Extract customerId from query parameters
  const customerId = new URLSearchParams(location.search).get("customerId");

  const [searchOpen, setSearchOpen] = useState(false);
  const [currentInvoice, setCurrentInvoice] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [invoiceStats, setInvoiceStats] = useState({
    wholeUnits: 0,
    halfUnits: 0,
    totalUnits: 0,
    currentTotal: 0,
    averagePerUnit: 0,
  });
  const [formData, setFormData] = useState({
    companyName: "",
    priceList: "",
    invoiceNumber: "",
  });
  const [selectedPricelist, setSelectedPricelist] = useState(null);
  const [customPartOpen, setCustomPartOpen] = useState(false);
  const [unitByWeightOpen, setUnitByWeightOpen] = useState(false);
  const [pricelistName, setPricelistName] = useState("");

  // Sort and filter state
  const [sortOption, setSortOption] = useState("newest");
  const [filterOption, setFilterOption] = useState("all");

  // User dialog state
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);

  const fetchInvoiceDetails = async (invoiceId) => {
    try {
      const response = await API.graphql(
        graphqlOperation(getInvoiceWithParts, { id: invoiceId }),
      );

      const invoice = response.data.getInvoice;

      // Calculate statistics
      let wholeCount = 0;
      let halfCount = 0;
      let totalValue = 0;

      if (invoice.parts && invoice.parts.items) {
        invoice.parts.items.forEach((part) => {
          if (part.grade === 1) wholeCount++;
          if (part.grade === 0.5) halfCount++;
          totalValue += parseFloat(part.price);
        });
      }

      // Calculate averages with and without aftermarkets
      const allParts = invoice.parts?.items || [];
      const partsForAverage = allParts.filter(
        (p) =>
          p?.status !== "empty" &&
          p?.status !== "aftermarket" &&
          String(p?.partNumber || "")
            .trim()
            .toLowerCase() !== "after market",
      );
      const allPartsForAverage = allParts.filter((p) => p?.status !== "empty");

      const stats = {
        wholeUnits: wholeCount,
        halfUnits: halfCount,
        totalUnits: wholeCount + halfCount,
        currentTotal: totalValue,
        averagePerUnit:
          partsForAverage.reduce(
            (sum, p) => sum + parseFloat(p.price || 0),
            0,
          ) / (partsForAverage.length || 1),
        averagePerUnitWithAftermarkets:
          allPartsForAverage.reduce(
            (sum, p) => sum + parseFloat(p.price || 0),
            0,
          ) / (allPartsForAverage.length || 1),
      };

      setInvoiceStats(stats);
      setCurrentInvoice(invoice);
    } catch (error) {
      console.error("Error fetching invoice details:", error);
    }
  };

  useEffect(() => {
    if (invoiceId) {
      fetchInvoiceDetails(invoiceId);
    }
  }, [invoiceId]);

  useEffect(() => {
    if (currentInvoice) {
      setFormData((prev) => ({
        ...prev,
        customerName: currentInvoice.customerName || "",
        companyName: currentInvoice.name || "",
        invoiceNumber: currentInvoice.invoiceNumber || "",
        email: currentInvoice.email || "",
        priceList: currentInvoice.priceList || "",
      }));
    }
  }, [currentInvoice]);

  const handlePricelistUpdate = (updatedUser) => {
    setSelectedPricelist(updatedUser.activePricelistID);
    setFormData((prev) => ({
      ...prev,
      priceList: updatedUser.activePricelistID,
    }));
  };

  const handleSubmit = async () => {
    try {
      const invoiceInput = {
        name: formData.companyName,
        invoiceNumber: formData.invoiceNumber,
        status: "open",
        ownerID: userRecord.id,
        recipientID: selectedPricelist,
        partCount: currentInvoice?.partCount || 0,
      };

      if (invoiceId) {
        invoiceInput.id = invoiceId;
        const response = await API.graphql(
          graphqlOperation(updateInvoice, { input: invoiceInput }),
        );
        const updatedInvoice = response.data.updateInvoice;
        setCurrentInvoice(updatedInvoice);
      } else {
        const response = await API.graphql(
          graphqlOperation(createInvoice, { input: invoiceInput }),
        );
        const newInvoice = response.data.createInvoice;
        setCurrentInvoice(newInvoice);

        await API.graphql(
          graphqlOperation(updateUser, {
            input: {
              id: userRecord.id,
              activeInvoiceID: newInvoice.id,
            },
          }),
        );
      }
    } catch (error) {
      console.error("Error saving invoice:", error);
    }
  };

  useEffect(() => {
    const fetchPricelistName = async () => {
      if (!userRecord?.activePricelistID) return;
      try {
        const response = await API.graphql(
          graphqlOperation(getPricelist, { id: userRecord.activePricelistID }),
        );
        const pricelist = response.data.getPricelist;
        if (pricelist && pricelist.name) {
          setPricelistName(pricelist.name);
        }
      } catch (error) {
        console.error("Error fetching pricelist name:", error);
      }
    };
    fetchPricelistName();
  }, [userRecord?.activePricelistID]);

  const fetchCustomers = async () => {
    try {
      console.log("═".repeat(80));
      console.log("[CreateInvoice] === fetchCustomers START ===");
      console.log("[CreateInvoice] userRecord:", userRecord);
      console.log("[CreateInvoice] userRecord.id:", userRecord?.id);
      console.log("[CreateInvoice] userRecord.email:", userRecord?.email);
      console.log("[CreateInvoice] userRecord.username:", userRecord?.username);
      console.log("[CreateInvoice] userRecord.isProvider:", userRecord?.isProvider);
      console.log("[CreateInvoice] userRecord.createdByUserId:", userRecord?.createdByUserId);
      console.log("═".repeat(80));

      const result = await fetchCustomersForProvider(userRecord);

      console.log("[CreateInvoice] fetchCustomersForProvider result:", result);

      if (result.success) {
        console.log("[CreateInvoice] ✅ Successfully fetched", result.data.length, "customers");
        setCustomers(result.data);
      } else {
        console.warn("[CreateInvoice] ❌ Failed to fetch customers:", result.message);
        console.warn("[CreateInvoice] Error code:", result.error);
        setCustomers([]);
      }
    } catch (error) {
      console.error("[CreateInvoice] ❌ Error fetching customers:", error);
      console.error("[CreateInvoice] Error message:", error.message);
      setCustomers([]);
    } finally {
      console.log("═".repeat(80));
      console.log("[CreateInvoice] === fetchCustomers END ===");
      console.log("═".repeat(80));
    }
  };

  useEffect(() => {
    console.log("[CreateInvoice] useEffect triggered");
    console.log("[CreateInvoice] userRecord:", userRecord);
    console.log("[CreateInvoice] userRecord?.id:", userRecord?.id);
    console.log("[CreateInvoice] userRecord?.email:", userRecord?.email);
    
    if (userRecord?.id) {
      console.log("[CreateInvoice] ✅ userRecord.id exists, calling fetchCustomers()");
      fetchCustomers();
    } else {
      console.warn("[CreateInvoice] ❌ userRecord.id does not exist, skipping fetchCustomers()");
    }
  }, [userRecord?.id, userRecord?.email]);

  const handleCustomerSelect = (customer) => {
    if (customer) {
      setSelectedCustomer(customer.id);
      setFormData((prev) => ({
        ...prev,
        companyName: customer.companyName || "",
        recipientID: customer.id,
      }));
    } else {
      setSelectedCustomer(null);
    }
  };

  const handleEditCustomer = (customer) => {
    setUserDialogOpen(true);
    setEditingCustomer(customer);
  };

  const getSortedAndFilteredParts = (parts) => {
    if (!parts || !Array.isArray(parts)) return [];

    let filteredParts = [...parts];
    if (filterOption === "wholeUnits") {
      filteredParts = parts.filter((part) => part.grade === 1);
    } else if (filterOption === "halfUnits") {
      filteredParts = parts.filter((part) => part.grade === 0.5);
    }

    switch (sortOption) {
      case "newest":
        return filteredParts.sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
        );
      case "oldest":
        return filteredParts.sort(
          (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
        );
      case "partNumber":
        return filteredParts.sort((a, b) =>
          a.partNumber.localeCompare(b.partNumber),
        );
      case "priceHighToLow":
        return filteredParts.sort((a, b) => (b.price || 0) - (a.price || 0));
      case "priceLowToHigh":
        return filteredParts.sort((a, b) => (a.price || 0) - (b.price || 0));
      default:
        return filteredParts;
    }
  };

  const handleAddEmptyPart = async () => {
    try {
      if (!currentInvoice) return;
      const input = {
        partNumber: "Empty",
        price: 0,
        grade: 0,
        status: "empty",
        addedByUser: userRecord.id,
        fromPricelist: userRecord.activePricelistID,
        invoicePartsId: currentInvoice.id,
      };
      await API.graphql(graphqlOperation(createPart, { input }));
      await API.graphql(
        graphqlOperation(updateInvoice, {
          input: {
            id: currentInvoice.id,
            partCount: (currentInvoice.partCount || 0) + 1,
          },
        }),
      );
      fetchInvoiceDetails(currentInvoice.id);
    } catch (e) {
      console.error("Error adding empty part:", e);
    }
  };

  const handleAddAftermarketPart = async () => {
    try {
      if (!currentInvoice || !userRecord?.activePricelistID) return;
      const plResp = await API.graphql(
        graphqlOperation(getPricelist, { id: userRecord.activePricelistID }),
      );
      const s3Key = plResp?.data?.getPricelist?.s3Key;
      if (!s3Key) throw new Error("Pricelist S3 key not found");

      const file = await Storage.get(s3Key, { download: true });
      const text = await file.Body.text();
      const json = JSON.parse(text);
      const item = (json || []).find(
        (row) =>
          String(row["PART NUMBER"] || "")
            .trim()
            .toLowerCase() === "after market",
      );
      const basePrice = item ? (item.adjustedPrice ?? item.PRICE ?? 0) : 0;

      const adjustedPrice = await calculateInvoicePrice(
        parseFloat(basePrice || 0),
        userRecord,
      );
      const price = Math.round(adjustedPrice);

      const input = {
        partNumber: "After Market",
        price: isNaN(price) ? 0 : price,
        grade: 1,
        status: "aftermarket",
        addedByUser: userRecord.id,
        fromPricelist: userRecord.activePricelistID,
        invoicePartsId: currentInvoice.id,
      };
      await API.graphql(graphqlOperation(createPart, { input }));
      await API.graphql(
        graphqlOperation(updateInvoice, {
          input: {
            id: currentInvoice.id,
            partCount: (currentInvoice.partCount || 0) + 1,
          },
        }),
      );
      fetchInvoiceDetails(currentInvoice.id);
    } catch (e) {
      console.error("Error adding aftermarket part:", e);
    }
  };

  return (
    <InnerPage>
      <Box
        sx={{
          backgroundColor: (theme) => theme.palette.background.default,
          minHeight: "100vh",
          padding: "20px",
          marginTop: "20px",
        }}
      >
        {!currentInvoice ? (
          <>
            <CustomerSelector
              onCustomerSelect={handleCustomerSelect}
              theme={theme}
            />

            {selectedCustomer && (
              <Box sx={{ mt: 4 }}>
                <CustomerCard
                  customer={
                    customers.find((c) => c.id === selectedCustomer) ||
                    customers.find((c) => c.id === formData.recipientID)
                  }
                  selected={true}
                  onSelect={handleCustomerSelect}
                  onEdit={handleEditCustomer}
                  theme={theme}
                />
              </Box>
            )}

            {selectedCustomer && (
              <Box
                sx={{
                  mt: 4,
                  mb: 2,
                  maxWidth: "800px",
                  margin: "0 auto",
                }}
              >
                <Typography
                  sx={{
                    color: theme.palette.text.primary,
                    fontSize: "18px",
                    marginBottom: "10px",
                  }}
                >
                  Invoice / PO Number
                </Typography>
                <TextField
                  fullWidth
                  value={formData.invoiceNumber}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      invoiceNumber: e.target.value,
                    }))
                  }
                  placeholder="i.e., HR 24001"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "30px",
                      backgroundColor: theme.palette.background.paper,
                      "& fieldset": {
                        borderColor: theme.palette.primary.main,
                        borderWidth: "2px",
                      },
                      "&:hover fieldset": {
                        borderColor: theme.palette.primary.main,
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: theme.palette.primary.main,
                      },
                    },
                    "& .MuiInputBase-input": {
                      color: theme.palette.text.primary,
                      padding: "15px 20px",
                      fontSize: "18px",
                    },
                  }}
                />
              </Box>
            )}

            {selectedCustomer && formData.invoiceNumber && (
              <Box sx={{ maxWidth: "800px", margin: "0 auto", mt: 2, mb: 4 }}>
                <Button
                  onClick={handleSubmit}
                  sx={{
                    backgroundColor: "#24e4ff",
                    color: "#000",
                    borderRadius: "30px",
                    padding: "15px",
                    fontSize: "20px",
                    fontWeight: "bold",
                    width: "100%",
                    "&:hover": {
                      backgroundColor: "#35d33d",
                    },
                  }}
                >
                  Create Invoice
                </Button>
              </Box>
            )}

            <UserDialog
              user={editingCustomer}
              open={userDialogOpen}
              onClose={() => {
                setUserDialogOpen(false);
                setEditingCustomer(null);
                fetchCustomers();
              }}
              onEditUser={(updatedUser) => {
                if (editingCustomer && updatedUser.id === editingCustomer.id) {
                  setEditingCustomer(updatedUser);
                }
              }}
            />
          </>
        ) : (
          <>
            <InvoiceHeader formData={formData} />
            <InvoiceStats invoiceStats={invoiceStats} />
            <PartsList
              currentInvoice={currentInvoice}
              getSortedAndFilteredParts={getSortedAndFilteredParts}
              sortOption={sortOption}
              setSortOption={setSortOption}
              filterOption={filterOption}
              setFilterOption={setFilterOption}
              fetchInvoiceDetails={fetchInvoiceDetails}
            />
            <ActionButtons
              setSearchOpen={setSearchOpen}
              setCustomPartOpen={setCustomPartOpen}
              setUnitByWeightOpen={setUnitByWeightOpen}
              currentInvoice={currentInvoice}
              formData={formData}
              invoiceStats={invoiceStats}
              onAddEmptyPart={handleAddEmptyPart}
              onAddAftermarketPart={handleAddAftermarketPart}
            />
            {/* Modals */}
            {(() => {
              const customerData = customers.find(
                (c) =>
                  c.id === selectedCustomer || c.id === formData.recipientID,
              );
              return (
                <SearchModal
                  open={searchOpen}
                  onClose={() => setSearchOpen(false)}
                  pricelistName={pricelistName}
                  currentInvoice={currentInvoice}
                  userRecord={userRecord}
                  customerData={customerData}
                  fetchInvoiceDetails={fetchInvoiceDetails}
                />
              );
            })()}
            <CustomPartModal
              open={customPartOpen}
              onClose={() => setCustomPartOpen(false)}
              currentInvoice={currentInvoice}
              userRecord={userRecord}
              fetchInvoiceDetails={fetchInvoiceDetails}
            />
            <UnitByWeightModal
              open={unitByWeightOpen}
              onClose={() => setUnitByWeightOpen(false)}
              currentInvoice={currentInvoice}
              userRecord={userRecord}
              fetchInvoiceDetails={fetchInvoiceDetails}
            />
          </>
        )}
      </Box>
    </InnerPage>
  );
}
